import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  CheckCircle,
  Award,
  FileText,
  Bell,
  BookOpen,
  ClipboardList,
  Loader2,
  AlertCircle,
  GraduationCap,
  Building2,
  User,
  Lock,
  History,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useClassDetail, useStudentAcademicYears } from '../hooks/useStudent';

// Tab Components
import AnnouncementsTab from '../components/class-detail/AnnouncementsTab';
import SyllabusTab from '../components/class-detail/SyllabusTab';
import ContentTab from '../components/class-detail/ContentTab';
import AssignmentsTab from '../components/class-detail/AssignmentsTab';
import GradesTab from '../components/class-detail/GradesTab';
import AttendanceTab from '../components/class-detail/AttendanceTab';
import ClassmatesTab from '../components/class-detail/ClassmatesTab';

type TabId = 'announcements' | 'syllabus' | 'content' | 'assignments' | 'grades' | 'attendance' | 'classmates';

interface NavItem {
  id: TabId;
  label: string;
  icon: React.ElementType;
  badge?: number | string;
  badgeColor?: string;
}

const ClassDetailPage: React.FC = () => {
  const { moduleElementId } = useParams<{ moduleElementId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const academicYearId = searchParams.get('academicYearId') || undefined;
  // Former group's course instance (set by the list when the student left that group)
  const groupeId = searchParams.get('groupeId') || undefined;
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const tenantId = user?.tenantId;

  // Get active tab from URL or default to 'announcements'
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const validTabs: TabId[] = ['announcements', 'syllabus', 'content', 'assignments', 'grades', 'attendance', 'classmates'];
  const activeTab: TabId = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'announcements';

  // Update tab in URL when changed
  const setActiveTab = (tab: TabId) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  };

  // Fetch class detail
  const { data: classDetail, isLoading, error, refetch } = useClassDetail(
    moduleElementId || null,
    academicYearId,
    groupeId
  );

  // A past year is consultation only: every tab reads that year, nothing is submittable
  const { data: academicYears = [] } = useStudentAcademicYears();
  const selectedYear = academicYearId ? academicYears.find((y) => y.id === academicYearId) : undefined;
  const isPastYear = selectedYear ? !selectedYear.isCurrent : false;
  // A former group's course (mid-year move, deleted group) is history: read-only,
  // and its classmates are not the student's current classmates
  const isFormerGroup = classDetail?.isFormerGroup === true;
  const isReadOnly = isPastYear || isFormerGroup;

  // Build navigation items
  const navItems: NavItem[] = [
    {
      id: 'announcements',
      label: 'Annonces',
      icon: Bell,
      badge: classDetail?.announcementsCount || undefined,
    },
    {
      id: 'syllabus',
      label: 'Syllabus',
      icon: FileText,
      badge: !classDetail?.syllabusUploaded ? '!' : undefined,
      badgeColor: 'bg-amber-100 text-amber-700',
    },
    {
      id: 'content',
      label: 'Contenu',
      icon: BookOpen,
      badge: classDetail?.materialsCount || undefined,
    },
    {
      id: 'assignments',
      label: 'Devoirs',
      icon: ClipboardList,
      badge: classDetail?.assignmentsCount || undefined,
    },
    {
      id: 'grades',
      label: 'Notes',
      icon: Award,
    },
    {
      id: 'attendance',
      label: 'Présences',
      icon: CheckCircle,
    },
    ...(isFormerGroup
      ? []
      : [
          {
            id: 'classmates' as TabId,
            label: 'Camarades',
            icon: Users,
            badge: classDetail?.totalStudents ? classDetail.totalStudents - 1 : undefined,
          },
        ]),
  ];

  // Handle back navigation (keep the consulted year selected on the list)
  const handleBack = () => {
    const params = academicYearId ? `?academicYearId=${academicYearId}` : '';
    navigate(`/institution/${tenantId}/student/mes-cours${params}`);
  };

  // Render tab content
  const renderTabContent = () => {
    if (!moduleElementId || !classDetail) return null;

    switch (activeTab) {
      case 'announcements':
        return (
          <AnnouncementsTab
            moduleElementId={moduleElementId}
            academicYearId={academicYearId}
            groupeId={groupeId}
          />
        );
      case 'syllabus':
        return <SyllabusTab moduleElementId={moduleElementId} academicYearId={academicYearId} groupeId={groupeId} />;
      case 'content':
        return <ContentTab moduleElementId={moduleElementId} academicYearId={academicYearId} groupeId={groupeId} />;
      case 'assignments':
        return (
          <AssignmentsTab
            moduleElementId={moduleElementId}
            academicYearId={academicYearId}
            groupeId={groupeId}
            readOnly={isReadOnly}
          />
        );
      case 'grades':
        return (
          <GradesTab moduleElementId={moduleElementId} academicYearId={academicYearId} groupeId={groupeId} />
        );
      case 'attendance':
        return (
          <AttendanceTab moduleElementId={moduleElementId} academicYearId={academicYearId} groupeId={groupeId} />
        );
      case 'classmates':
        if (isFormerGroup) return null;
        return <ClassmatesTab groupeId={classDetail.groupeId} />;
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error || !classDetail) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft size={20} />
            <span>Retour à mes cours</span>
          </button>
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-6 w-6" />
            <div>
              <p className="font-medium">Erreur lors du chargement</p>
              <p className="text-sm">{(error as Error)?.message || 'Cours non trouvé'}</p>
            </div>
            <button
              onClick={() => refetch()}
              className="ml-auto px-4 py-2 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Compact Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm mb-3 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Retour à mes cours</span>
          </button>

          {/* Title and badges */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <h1 className="text-xl font-bold text-gray-900">
              {classDetail.moduleElementName}
            </h1>

            {/* Badges - compact */}
            <div className="flex flex-wrap items-center gap-2">
              {selectedYear && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                  {selectedYear.name}
                  {selectedYear.isCurrent ? ' (En cours)' : ''}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                <GraduationCap className="h-3.5 w-3.5" />
                {classDetail.groupeName}
              </span>
              {isFormerGroup && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                  <History className="h-3.5 w-3.5" />
                  Ancien groupe
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                <User className="h-3.5 w-3.5" />
                Professeur : {classDetail.professorName}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-medium rounded-full">
                <GraduationCap className="h-3.5 w-3.5" />
                {classDetail.niveau === 1 ? '1ère' : `${classDetail.niveau}ème`} Année
              </span>
              {classDetail.campusName && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                  <Building2 className="h-3 w-3" />
                  {classDetail.campusName}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Past year: consultation only */}
      {isPastYear && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 text-sm flex-shrink-0">
          <Lock className="h-4 w-4 flex-shrink-0" />
          <span>Consultation d'une année passée — lecture seule</span>
        </div>
      )}

      {/* Former group: the notes and attendance recorded there stay consultable, nothing can be submitted */}
      {isFormerGroup && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-800 px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-2 text-sm flex-shrink-0">
          <History className="h-4 w-4 flex-shrink-0" />
          <span>
            Ce cours appartient à votre ancien groupe ({classDetail.groupeName}) : vos notes et présences y sont
            conservées en lecture seule.
          </span>
        </div>
      )}

      {/* Main content with sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Vertical Sidebar Navigation */}
        <nav className="w-56 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
          <div className="py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;

              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                    ${isActive
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${item.badgeColor || 'bg-gray-100 text-gray-600'}`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-5xl">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClassDetailPage;
